import React from 'react'
import { Button, Input, Select } from 'antd'
import {
    DownloadOutlined,
    SearchOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { FilterActions, FilterBar, FilterField } from '../common/FilterBar'
import { PaginatedListToolbar } from '../common/PaginatedListTable'

interface HistoryActionsProps {
    hasRecords: boolean
    loading: boolean
    canExport: boolean
    canViewExportTasks?: boolean
    exporting: boolean
    deviceNameSearchValue?: string
    pestTypeOptions?: Array<{ label: string; value: string }>
    pestTypeSearchValue?: string[]
    showDeviceNameSearch?: boolean
    showPestTypeSearch?: boolean
    onExport: () => void
    onViewExportTasks?: () => void
    onDeviceNameSearch?: () => void
    onDeviceNameSearchChange?: (value: string) => void
    onDeviceNameSearchReset?: () => void
    onPestTypeSearchChange?: (value: string[]) => void
}

export const HistoryActions: React.FC<HistoryActionsProps> = ({
    hasRecords,
    loading,
    canExport,
    canViewExportTasks = false,
    exporting,
    deviceNameSearchValue = '',
    pestTypeOptions = [],
    pestTypeSearchValue = [],
    showDeviceNameSearch = false,
    showPestTypeSearch = false,
    onExport,
    onViewExportTasks,
    onDeviceNameSearch,
    onDeviceNameSearchChange,
    onDeviceNameSearchReset,
    onPestTypeSearchChange,
}) => {
    const { t } = useTranslation('history')
    const showListActions =
        (canViewExportTasks && onViewExportTasks) || (hasRecords && canExport)

    return (
        <>
            {(showDeviceNameSearch || showPestTypeSearch) && (
                <FilterBar>
                    {showDeviceNameSearch && (
                        <FilterField
                            className="history-device-search-field"
                            label={t('filters.deviceSearch')}
                        >
                            <Input
                                allowClear
                                aria-label={t('filters.deviceSearch')}
                                onChange={(event) =>
                                    onDeviceNameSearchChange?.(
                                        event.target.value
                                    )
                                }
                                onPressEnter={() => onDeviceNameSearch?.()}
                                placeholder={t(
                                    'filters.deviceSearchPlaceholder'
                                )}
                                value={deviceNameSearchValue}
                            />
                        </FilterField>
                    )}
                    {showPestTypeSearch && (
                        <FilterField label={t('filters.pestType')}>
                            <Select<string[]>
                                allowClear
                                aria-label={t('filters.pestType')}
                                maxTagCount="responsive"
                                mode="multiple"
                                onChange={(value) =>
                                    onPestTypeSearchChange?.(value)
                                }
                                optionFilterProp="label"
                                options={pestTypeOptions}
                                placeholder={t('filters.pestTypePlaceholder')}
                                showSearch
                                value={pestTypeSearchValue}
                            />
                        </FilterField>
                    )}
                    <FilterActions>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            loading={loading}
                            onClick={onDeviceNameSearch}
                        >
                            {t('actions.search')}
                        </Button>
                        <Button onClick={onDeviceNameSearchReset}>
                            {t('actions.reset')}
                        </Button>
                    </FilterActions>
                </FilterBar>
            )}
            {showListActions && (
                <PaginatedListToolbar>
                    {canViewExportTasks && onViewExportTasks && (
                        <Button
                            icon={<UnorderedListOutlined />}
                            onClick={onViewExportTasks}
                        >
                            {t('actions.exportTasks')}
                        </Button>
                    )}
                    {hasRecords && canExport && (
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={onExport}
                            loading={exporting}
                        >
                            {t('actions.exportDataset')}
                        </Button>
                    )}
                </PaginatedListToolbar>
            )}
        </>
    )
}
